import { useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { spanActionLabel, type SpanEntry } from '@/utils/spanAction'
import { DAY_INT_TO_LABEL } from '@/utils/teacherContextGrid'

interface GapCellPopoverProps {
  entry: SpanEntry
  neighborUp: SpanEntry | null
  neighborDown: SpanEntry | null
  anchor: DOMRect
  busy?: boolean
  error?: string | null
  canExtendUp?: boolean
  canExtendDown?: boolean
  onExtend: (direction: 'up' | 'down') => void
  onEditField: (field: 'subject' | 'room' | 'klass') => void
  onDelete: () => void
  onClose: () => void
}

export function GapCellPopover({
  entry,
  neighborUp,
  neighborDown,
  anchor,
  busy,
  error,
  canExtendUp = true,
  canExtendDown = true,
  onExtend,
  onEditField,
  onDelete,
  onClose,
}: GapCellPopoverProps) {
  const ref = useRef<HTMLDivElement>(null)
  const day = entry.day_of_week != null ? DAY_INT_TO_LABEL[entry.day_of_week] : ''
  const slot = entry.lesson_number != null ? `L${entry.lesson_number}` : (entry.time_slot_name || '')
  const pos = useMemo(() => {
    const w = 240
    const gap = 8
    const margin = 8
    let left = anchor.right + gap
    if (left + w > window.innerWidth - margin) left = Math.max(margin, anchor.left - w - gap)
    let top = anchor.top
    const h = 280
    if (top + h > window.innerHeight - margin) top = Math.max(margin, window.innerHeight - margin - h)
    return { left, top, width: w }
  }, [anchor])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [onClose])

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label="Zelle bearbeiten"
      className="fixed z-[70] rounded-lg border border-gray-200 bg-white p-3 shadow-xl"
      style={{ left: pos.left, top: pos.top, width: pos.width }}
    >
      <h6 className="text-sm font-semibold">{[day, slot].filter(Boolean).join(' ')}</h6>
      <p className="text-xs text-gray-500 mb-2">
        {entry.ampel_reasons?.length ? entry.ampel_reasons.join(', ') : 'Zelle'}
        {entry.pdf_raw_text ? ` · PDF: ${entry.pdf_raw_text}` : ''}
      </p>
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      <button
        type="button"
        disabled={busy || !canExtendUp}
        onClick={() => onExtend('up')}
        className="mb-1 block w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-left text-xs disabled:opacity-40 hover:bg-gray-50"
      >
        {spanActionLabel('up', entry, neighborUp)}
      </button>
      <button
        type="button"
        disabled={busy || !canExtendDown}
        onClick={() => onExtend('down')}
        className="mb-2 block w-full rounded-md border border-blue-600 bg-blue-600 px-2 py-1.5 text-left text-xs font-medium text-white disabled:opacity-40 hover:bg-blue-700"
      >
        {spanActionLabel('down', entry, neighborDown)}
      </button>
      <div className="border-t border-gray-100 pt-2 space-y-1">
        <button
          type="button"
          disabled={busy}
          onClick={() => onEditField('subject')}
          className="block w-full rounded-md border border-gray-200 px-2 py-1.5 text-left text-xs hover:bg-gray-50"
        >
          Fach zuordnen…
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onEditField('klass')}
          className="block w-full rounded-md border border-gray-200 px-2 py-1.5 text-left text-xs hover:bg-gray-50"
        >
          Klasse bearbeiten…
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onEditField('room')}
          className="block w-full rounded-md border border-gray-200 px-2 py-1.5 text-left text-xs hover:bg-gray-50"
        >
          Raum bearbeiten…
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onDelete}
          className="block w-full rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-left text-xs font-medium text-red-700 hover:bg-red-100"
        >
          Löschen
        </button>
      </div>
    </div>,
    document.body,
  )
}
